import type { ReactNode } from "react";
import { FONTES_DE_ETIQUETA, LIMITES_DA_UNICA, type EtiquetaUnica } from "@bookdental/schemas";
import { Checkbox } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { ListaDeCampos } from "./ListaDeCampos";
import { ImpressaoDireta } from "./ImpressaoDireta";
import { OpcaoQrDoCatalogo } from "./OpcaoQrDoCatalogo";

const NOME_DA_FONTE: Record<string, string> = { compacta: "Compacta", normal: "Normal", grande: "Grande" };
const TAMANHOS_PRONTOS = [
  { rotulo: "10 × 15 cm", largura: 100, altura: 150 },
  { rotulo: "10 × 10 cm", largura: 100, altura: 100 },
  { rotulo: "10 × 7,5 cm", largura: 100, altura: 75 },
];
const labelCls = "mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground";
const dicaCls = "text-[11px] text-zinc-500 dark:text-muted-foreground";
const campoCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";

function Bloco({ titulo, descricao, children }: { titulo: string; descricao?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-border dark:bg-card sm:p-4">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{titulo}</h3>
      {descricao && <p className={cn(dicaCls, "mt-0.5")}>{descricao}</p>}
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function Medida({ rotulo, valor, limites, onChange }: { rotulo: string; valor: number; limites: readonly [number, number]; onChange: (v: number) => void }) {
  const [min, max] = limites;
  return (
    <label>
      <span className={labelCls}>{rotulo}</span>
      <div className="relative">
        <input
          type="number"
          aria-label={`${rotulo} (mm)`}
          min={min}
          max={max}
          value={valor}
          onChange={(e) => onChange(Math.round(Number(e.target.value) || 0))}
          onBlur={() => onChange(Math.min(max, Math.max(min, valor)))}
          className={cn(campoCls, "px-2 pr-8 sm:pr-10")}
        />
        <span aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 sm:right-3 -translate-y-1/2 text-xs text-zinc-400">mm</span>
      </div>
    </label>
  );
}

function Opcao({ checked, onChange, titulo, dica }: { checked: boolean; onChange: (v: boolean) => void; titulo: string; dica?: string }) {
  return (
    <label className="flex items-start gap-2 text-sm text-zinc-700 dark:text-foreground">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <span>
        {titulo}
        {dica && <span className={cn(dicaCls, "block")}>{dica}</span>}
      </span>
    </label>
  );
}

/** Etiqueta única (uma por serviço): tamanho, altura da expedição, campos das duas partes e impressão. */
export function ControlesDaEtiquetaUnica({ config, onChange }: { config: EtiquetaUnica; onChange: (c: EtiquetaUnica) => void }) {
  const muda = (parcial: Partial<EtiquetaUnica>) => onChange({ ...config, ...parcial });
  const sobraProducao = config.altura - config.alturaDaExpedicao - (config.alturaDaExpedicao ? 11 : 5);
  const comExpedicao = config.alturaDaExpedicao > 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600 dark:text-muted-foreground">
        Uma etiqueta por serviço: a parte de cima vai com a peça na bancada, a de baixo vai na caixa. Recorte na linha pontilhada.
      </p>

      <Bloco titulo="Tamanho">
        <div className="flex flex-wrap gap-1.5">
          {TAMANHOS_PRONTOS.map((t) => (
            <button
              key={t.rotulo}
              type="button"
              onClick={() => muda({ largura: t.largura, altura: t.altura })}
              className={cn(
                "h-8 rounded-md border px-2.5 text-[12px] font-semibold transition-colors",
                config.largura === t.largura && config.altura === t.altura
                  ? "border-brand-600 bg-brand-50 text-brand-700 dark:bg-primary/10 dark:text-primary"
                  : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground",
              )}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Medida rotulo="Largura" valor={config.largura} limites={LIMITES_DA_UNICA.largura} onChange={(largura) => muda({ largura })} />
          <Medida rotulo="Altura" valor={config.altura} limites={LIMITES_DA_UNICA.altura} onChange={(altura) => muda({ altura })} />
          <Medida rotulo="Expedição" valor={config.alturaDaExpedicao} limites={LIMITES_DA_UNICA.expedicao} onChange={(alturaDaExpedicao) => muda({ alturaDaExpedicao })} />
        </div>
        <p className={cn("text-[11px]", sobraProducao < 40 ? "text-amber-600" : "text-zinc-500 dark:text-muted-foreground")}>
          {comExpedicao
            ? `A produção fica com cerca de ${Math.max(0, sobraProducao)} mm de altura.`
            : "Expedição em 0 mm: sai só a parte de produção, sem linha de corte."}
          {sobraProducao < 40 && " Pouco espaço: alguns campos podem ser cortados."}
        </p>
      </Bloco>

      <Bloco titulo="Aparência">
        <label className="block sm:w-48">
          <span className={labelCls}>Letra</span>
          <select value={config.fonte} onChange={(e) => muda({ fonte: e.target.value as EtiquetaUnica["fonte"] })} className={campoCls}>
            {FONTES_DE_ETIQUETA.map((f) => (
              <option key={f} value={f}>
                {NOME_DA_FONTE[f]}
              </option>
            ))}
          </select>
        </label>
        <Opcao
          checked={config.qr}
          onChange={(qr) => muda({ qr })}
          titulo="QR code na parte de produção"
          dica="Lido com o celular, abre o pedido no DentalBook (precisa estar logado no laboratório)."
        />
        <Opcao checked={config.logo} onChange={(logo) => muda({ logo })} titulo="Logo do laboratório na parte de expedição" />
        {comExpedicao && <OpcaoQrDoCatalogo checked={config.qrCatalogo} onChange={(qrCatalogo) => muda({ qrCatalogo })} onde="na parte de expedição" />}
      </Bloco>

      <Bloco titulo="Campos" descricao="Marque o que aparece e arraste para mudar a ordem.">
        <div className={cn("grid gap-4", comExpedicao && "sm:grid-cols-2")}>
          <div>
            <p className={labelCls}>Produção</p>
            <ListaDeCampos campos={config.camposProducao} onChange={(camposProducao) => muda({ camposProducao: camposProducao as EtiquetaUnica["camposProducao"] })} />
            <p className={cn(dicaCls, "mt-1.5")}>O código do serviço vai sempre no topo; o prazo fica embaixo dele.</p>
          </div>
          {comExpedicao && (
            <div>
              <p className={labelCls}>Expedição</p>
              <ListaDeCampos campos={config.camposExpedicao} onChange={(camposExpedicao) => muda({ camposExpedicao: camposExpedicao as EtiquetaUnica["camposExpedicao"] })} />
              <p className={cn(dicaCls, "mt-1.5")}>Clínica e endereço ocupam a largura toda; os outros, meia.</p>
            </div>
          )}
        </div>
      </Bloco>

      <Bloco titulo="Impressão">
        <Opcao
          checked={config.imprimirAoAceitar}
          onChange={(imprimirAoAceitar) => muda({ imprimirAoAceitar })}
          titulo="Imprimir ao aceitar o pedido"
          dica="Aceitou, a impressão das etiquetas de todos os serviços já abre."
        />
        {config.imprimirAoAceitar && <ImpressaoDireta />}
      </Bloco>
    </div>
  );
}
